import { FilterStateController } from '@/components/dataGrid/entities/types';
import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseReadOneOptions<Response, Filter> {
  source: (filters?: Filter, companyId?: string) => Promise<Response> | Response;
  paramsToFilter?: FilterStateController;
  companyId?: string;
  externalLoading?: boolean;
}

export const useRequestReadOne = <Response, Filter = Response>({
  source,
  paramsToFilter,
  companyId = '',
  externalLoading,
}: UseReadOneOptions<Response, Filter>) => {
  const [data, setData] = useState<Response>();
  const [loading, setLoading] = useState(true);

  const sourceRef = useRef(source);
  const canLoadDataRef = useRef(true);

  const loadData = useCallback(
    async (filters?: Filter, companyIdToFilter?: string) => {
      if (!canLoadDataRef.current || externalLoading) {
        return;
      }

      await tryOrCatchMessageError(
        async () => {
          const response = await sourceRef.current(filters, companyIdToFilter);

          setData(response);
        },
        {
          tryBefore: () => {
            setLoading(true);
            canLoadDataRef.current = false;
          },
          tryAfter: () => {
            setLoading(false);
            canLoadDataRef.current = true;
          },
          messageErrorDefault: 'Não foi possível carregar o registro, tente novamente mais tarde.',
        },
      );
    },
    [externalLoading],
  );

  useEffect(() => {
    if (!externalLoading) {
      sourceRef.current = source;
    }
  }, [externalLoading, source]);

  useEffect(() => {
    loadData(paramsToFilter as Filter, companyId);
  }, [companyId, loadData, paramsToFilter]);

  return {
    data,
    loading,
    loadData,
  };
};
